import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApprovalDecision, InboxEntry } from '@this+that/models';
import log from 'loglevel';

import {
  BRAIN_SIGNAL_REVIEW_GENERIC_ERROR_KEY,
  isBrainSignalReviewAlreadyDecided,
  resolveBrainSignalReviewErrorKey,
} from './brainSignalReviewErrorKey';
import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useRespondToBrainSignalReviewService } from '../../services/brainSignals.service';
import { useUnreadStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

const SUCCESS_TOAST_KEY: Record<ApprovalDecision, string> = {
  [ApprovalDecision.APPROVED]: 'doBox.brainSignalReview.approved',
  [ApprovalDecision.REJECTED]: 'doBox.brainSignalReview.rejected',
};

const ALREADY_DECIDED_KEY = 'doBox.brainSignalReview.alreadyDecided';

export const useRespondToBrainSignalReview = () => {
  const { t } = useTranslation();
  const { respondToBrainSignalReview: respondService } =
    useRespondToBrainSignalReviewService();
  const { removeInboxEntry } = useActiveInboxEntriesSlice();
  const { markAsRead } = useUnreadStore();
  const { successMessage, warningMessage, errorMessage } = useCustomToast();
  const [isResponding, setIsResponding] = useState(false);

  const settleEntry = useCallback(
    (entry: InboxEntry) => {
      removeInboxEntry(entry.id);
      markAsRead(entry.id, entry.taskGroupId);
    },
    [removeInboxEntry, markAsRead],
  );

  const respondToBrainSignalReview = useCallback(
    async (entry: InboxEntry, decision: ApprovalDecision): Promise<boolean> => {
      const ref = entry.brainSignalReview;
      if (!ref) {
        errorMessage(t(BRAIN_SIGNAL_REVIEW_GENERIC_ERROR_KEY));
        return false;
      }

      setIsResponding(true);
      try {
        await respondService(ref.teamId, ref.signalId, ref.version, decision);
        settleEntry(entry);
        successMessage(t(SUCCESS_TOAST_KEY[decision]));
        return true;
      } catch (error) {
        if (isBrainSignalReviewAlreadyDecided(error)) {
          settleEntry(entry);
          warningMessage(t(ALREADY_DECIDED_KEY));
          return true;
        }
        log.error('respondToBrainSignalReview error', {
          entryId: entry.id,
          decision,
          error,
        });
        errorMessage(t(resolveBrainSignalReviewErrorKey(error)));
        return false;
      } finally {
        setIsResponding(false);
      }
    },
    [
      respondService,
      settleEntry,
      successMessage,
      warningMessage,
      errorMessage,
      t,
    ],
  );

  return { respondToBrainSignalReview, isResponding };
};
